/**
 * Utilities for handling user login redirects
 */

/**
 * Default pages to redirect to if no specific redirect URL is provided
 */
const DEFAULT_REDIRECTS = {
  authenticated: '/courses', // Default page for authenticated users
  fallback: '/', // Fallback page
};

/**
 * URLs that should not be used as redirect targets
 */
const INVALID_REDIRECT_URLS = [
  '/login',
  '/register',
  '/logout',
  '/auth',
];

/**
 * Check if a URL is valid for redirection
 */
export const isValidRedirectUrl = (url: string | null): boolean => {
  if (!url) return false;
  
  // Check if URL is in the invalid list
  if (INVALID_REDIRECT_URLS.some(invalid => url.startsWith(invalid))) {
    return false;
  }
  
  // Make sure it's a relative URL (starts with /)
  if (!url.startsWith('/')) {
    return false;
  }
  
  return true;
};

/**
 * Get the appropriate redirect URL after login
 */
export const getPostLoginRedirectUrl = (
  storeUrl: string | null,
  locationUrl: string | null,
  defaultUrl?: string
): string => {
  // Priority order:
  // 1. Store redirect URL (if valid)
  // 2. Location state URL (if valid)
  // 3. Default URL parameter
  // 4. Default authenticated page
  // 5. Fallback page
  
  if (isValidRedirectUrl(storeUrl)) {
    return storeUrl!;
  }
  
  if (isValidRedirectUrl(locationUrl)) {
    return locationUrl!;
  }
  
  if (defaultUrl && isValidRedirectUrl(defaultUrl)) {
    return defaultUrl;
  }
  
  return DEFAULT_REDIRECTS.authenticated;
};

/**
 * Capture the current URL for post-login redirect
 */
export const captureRedirectUrl = (location: Location): string => {
  return location.pathname + location.search + location.hash;
};

/**
 * Check if the current page requires authentication for course registration
 */
export const isProtectedCourseAction = (pathname: string): boolean => {
  // Course detail pages where users might try to register
  return /^\/courses\/\d+/.test(pathname);
};

/**
 * Generate a user-friendly message for redirect scenarios
 */
export const getRedirectMessage = (originalUrl: string | null): string => {
  if (!originalUrl) {
    return "Đăng nhập thành công! Chào mừng bạn quay trở lại.";
  }
  
  if (isProtectedCourseAction(originalUrl)) {
    return "Đăng nhập thành công! Bạn có thể tiếp tục đăng ký khóa học.";
  }
  
  return "Đăng nhập thành công! Đang chuyển hướng...";
};